import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, TextField, Grid, Autocomplete, CircularProgress, ToggleButtonGroup, ToggleButton, Button, Alert, Snackbar, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getData, postData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';
import MissingDepartmentsDialog from '../../common/MissingDepartmentsDialog';

const STORY_POINTS_CONFIG = [
    { value: 1, label: '1', color: '#10b981', meaning: 'Very small work', example: 'Text change or minor CSS tweak' },
    { value: 2, label: '2', color: '#10b981', meaning: 'Small', example: 'UI alignment or simple component' },
    { value: 3, label: '3', color: '#10b981', meaning: 'Medium', example: 'API integration for a single endpoint' },
    { value: 5, label: '5', color: '#10b981', meaning: 'Moderate', example: 'Complex form with validation logic' },
    { value: 8, label: '8', color: '#3b82f6', meaning: 'Complex', example: 'Reports, filters, or multi-step logic' },
    { value: 13, label: '13', color: '#3b82f6', meaning: 'Very complex', example: 'Full module or complex integration' },
    { value: 21, label: '21', color: '#f59e0b', meaning: 'Huge', example: 'Complete system architecture or epic-level task' },
    { value: 34, label: '34', color: '#ef4444', meaning: 'Too Large', example: 'Needs to be broken down further' },
];

function CreateUserStory({ projectData, epicData, onCancel, onClose, onSuccess }) {
    const navigate = useNavigate();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const userRole = userData?.user_type;
    const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState({
        projects: false,
        po: false
    });
    const [showMissingDeptDialog, setShowMissingDeptDialog] = useState(false);
    const [selectedProjectDetails, setSelectedProjectDetails] = useState(null);

    const showProjectSelector = !projectData && !epicData;

    const dateSource = useMemo(() => {
        if (epicData?.id) return 'Epic';
        if (projectData?.id || selectedProject?.id) return 'Project';
        return 'Default';
    }, [epicData, projectData, selectedProject]);

    const minAllowedDate = useMemo(() => {
        const today = dayjs().startOf('day');
        const date = epicData?.start_date || projectData?.start_date || selectedProject?.start_date;
        return date ? dayjs(date) : today;
    }, [epicData, projectData, selectedProject]);

    const maxAllowedDate = useMemo(() => {
        const date = epicData?.end_date || projectData?.end_date || selectedProject?.end_date;
        return date ? dayjs(date) : null;
    }, [epicData, projectData, selectedProject]);

    useEffect(() => {
        setFormData(prev => {
            let nextStart = prev.startDate;
            let nextEnd = prev.endDate;

            if (nextStart && nextStart.isBefore(minAllowedDate, 'day')) {
                nextStart = minAllowedDate;
            }
            if (maxAllowedDate && nextStart && nextStart.isAfter(maxAllowedDate, 'day')) {
                nextStart = maxAllowedDate;
            }

            const effectiveMinEnd = nextStart || minAllowedDate;
            if (nextEnd && nextEnd.isBefore(effectiveMinEnd, 'day')) {
                nextEnd = effectiveMinEnd;
            }
            if (maxAllowedDate && nextEnd && nextEnd.isAfter(maxAllowedDate, 'day')) {
                nextEnd = maxAllowedDate;
            }

            return {
                ...prev,
                startDate: nextStart,
                endDate: nextEnd,
            };
        });
    }, [minAllowedDate, maxAllowedDate]);

    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [epicInfo, setEpicInfo] = useState({});
    const [apiError, setApiError] = useState(null);

    const activeProjectId = epicData?.project_id || projectData?.id || selectedProject?.id;
    const activeProjectTitle = epicData?.project_title || projectData?.project_title || selectedProject?.project_title;

    const [data, setData] = useState({ po: [], tl: [], loadingPO: false, loadingTL: false });

    const [formData, setFormData] = useState({
        title: '',
        priority: 'Medium',
        storyPoints: 0,
        description: 'As a [user type], I want [some goal], so that [reason].',
        startDate: minAllowedDate,
        endDate: null,
        acceptanceCriteria: '',
        productOwnerName: '',
        teamLeadName: '',
        product_owner_id: null,
        team_lead_id: null,
    });

    useEffect(() => {
        const fetchDetails = async () => {
            const userDataRaw = localStorage.getItem('userData');
            const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
            const orgId = userData?.organization_id;
            const currentEpicId = epicData?.id;

            if (!activeProjectId) return;
            setData(prev => ({ ...prev, loadingPO: true, loadingTL: true }));

            try {
                const [poRes, tlRes, projectRes, epicRes] = await Promise.all([
                    getData(API_ENDPOINTS.fetchProductOwnersForProject(activeProjectId)),
                    getData(API_ENDPOINTS.fetchTeamLeadsForOrganization(orgId)),
                    getData(API_ENDPOINTS.fetchProjectById(activeProjectId)),
                    currentEpicId ? getData(API_ENDPOINTS.fetchEpicById(currentEpicId)) : Promise.resolve(null)
                ]);

                const projectDetails = projectRes?.message?.data;
                const teamLeader = projectDetails?.team_leader;
                const fetchedEpicInfo = epicRes?.message?.epic;

                setData({
                    po: poRes?.result || [],
                    tl: tlRes?.message?.users || [],
                    loadingPO: false,
                    loadingTL: false
                });

                setFormData(prev => ({
                    ...prev,
                    teamLeadName: teamLeader ? `${teamLeader.first_name} ${teamLeader.last_name}` : 'Not Assigned',
                    team_lead_id: teamLeader?.id || null,
                    productOwnerName: fetchedEpicInfo?.product_owner_name || 'Not Assigned',
                    product_owner_id: fetchedEpicInfo?.product_owner_id || null
                }));

                setSelectedProjectDetails(projectDetails);

                // Check if departments are empty
                const hasDepartments = projectDetails?.departments && projectDetails.departments.length > 0;
                if (!hasDepartments) {
                    setShowMissingDeptDialog(true);
                }

                if (fetchedEpicInfo) setEpicInfo(fetchedEpicInfo);
            } catch (err) {
                setData(prev => ({ ...prev, loadingPO: false, loadingTL: false }));
            }
        };

        if (activeProjectId) fetchDetails();
    }, [activeProjectId, epicData?.id]);

    const handleRefreshProjectDetails = async () => {
        if (!activeProjectId) return;
        try {
            const res = await getData(API_ENDPOINTS.fetchProjectById(activeProjectId));
            if (res.status === "SUCCESS") {
                const projectDetails = res.message.data || null;
                setSelectedProjectDetails(projectDetails);

                // Check if departments are now present
                const hasDepartments = projectDetails?.departments && projectDetails.departments.length > 0;
                if (hasDepartments) {
                    setShowMissingDeptDialog(false);
                }
            }
        } catch (error) {
            // Handle error silently
        }
    };

    useEffect(() => {
        const fetchProjects = async () => {
            if (!userId) return;
            try {
                setLoading(prev => ({ ...prev, projects: true }));
                const res = await getData(API_ENDPOINTS.fetchProjectsBySupervisor(userId));
                if (res.status === "SUCCESS") {
                    const fetchedProjects = res.message.data || [];
                    setProjects(fetchedProjects);
                    if (projectData && !fetchedProjects.find(p => p.id === projectData.id)) {
                        setProjects(prev => [projectData, ...prev]);
                    }
                }
            } catch (error) {
                return null;
            } finally {
                setLoading(prev => ({ ...prev, projects: false }));
            }
        };
        fetchProjects();
    }, [userId, projectData]);

    const validate = () => {
        let tempErrors = {};
        if (showProjectSelector && !selectedProject?.id) tempErrors.selectedProject = "Project is required";
        if (!formData.title.trim()) tempErrors.title = "Title is required";
        if (!formData.description.trim() || formData.description.includes("[user type]")) tempErrors.description = "Provide valid story description";
        if (!formData.storyPoints) tempErrors.storyPoints = "Select story points";
        if (!formData.startDate) tempErrors.startDate = "Start date is required";
        if (!formData.endDate) tempErrors.endDate = "End date is required";
        if (!formData.acceptanceCriteria.trim()) tempErrors.acceptanceCriteria = "Criteria is required";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const updateField = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: false }));
        // Clear the budget error when user changes story points or dates
        if (apiError) setApiError(null);
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSubmitting(true);

        const payload = {
            title: formData.title,
            description: formData.description,
            priority: formData.priority,
            start_date: formData.startDate.format('YYYY-MM-DD'),
            due_date: formData.endDate.format('YYYY-MM-DD'),
            epic_id: projectData ? null : (epicData?.id || null),
            projectId: activeProjectId,
            story_points: formData.storyPoints,
            acceptance_criteria: formData.acceptanceCriteria,
            product_owner_id: formData.product_owner_id,
            team_lead_id: formData.team_lead_id,
        };

        try {
            const res = await postData(API_ENDPOINTS.createUserStory, payload);
            if (res.status === "SUCCESS") {
                setSnackbar({ open: true, message: "User Story created!", severity: 'success' });
                setTimeout(() => {
                    if (onCancel) onCancel();
                    if (onSuccess) onSuccess();
                    navigate(`${roleBasePath}/user-stories?projectId=${activeProjectId}&projectTitle=${activeProjectTitle}${epicData?.id ? `&epicId=${epicData.id}` : ''}`);
                }, 600);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || "Submit failed";
            setApiError(errorMsg);

            // Also keep snackbar as a fallback if you wish
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const currentPointDetails = STORY_POINTS_CONFIG.find(p => p.value === formData.storyPoints);

    const handleCancelClick = () => {
        if (projectData && onClose) {
            onClose();
        } else if (onCancel) {
            onCancel();
        } else {
            navigate(-1);
        }
    };


    return (
        <Box sx={{ width: '100%', bgcolor: '#fff' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                        <Grid container spacing={2} sx={{ p: 3 }}>
                            <Grid size={{ xs: 12 }}>
                                <TextField fullWidth size="small" label="User Story Title *" value={formData.title}
                                    error={!!errors.title} helperText={errors.title}
                                    onChange={(e) => updateField('title', e.target.value)} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4 }}>
                                {showProjectSelector ? (
                                    <Autocomplete
                                        options={projects}
                                        getOptionLabel={(option) => option.project_title || ""}
                                        size="small"
                                        loading={loading.projects}
                                        value={selectedProject}
                                        onChange={(_, newValue) => {
                                            setSelectedProject(newValue);
                                            if (newValue) {
                                                setErrors(prev => ({ ...prev, selectedProject: '' }));
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Project *"
                                                error={!!errors.selectedProject}
                                                helperText={errors.selectedProject}
                                                placeholder={loading.projects ? "Loading..." : "Search project..."}
                                            />
                                        )}
                                    />
                                ) : (
                                    <TextField fullWidth size="small" label="Project" value={activeProjectTitle || 'Not Assigned'} disabled />
                                )}
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4 }}>
                                {epicInfo?.product_owner_id ? (
                                    <TextField fullWidth size="small" label="Product Owner" value={formData.productOwnerName} disabled />
                                ) : (
                                    <Autocomplete size="small" options={data.po || []} loading={data.loadingPO}
                                        getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
                                        value={data.po?.find(p => p.id === formData.product_owner_id) || null}
                                        onChange={(_, v) => setFormData(prev => ({ ...prev, product_owner_id: v?.id || null, productOwnerName: v ? `${v.first_name} ${v.last_name}` : '' }))}
                                        renderInput={(params) => <TextField {...params} label="Select Product Owner *" error={!!errors.product_owner_id} />}
                                    />
                                )}
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField fullWidth size="small" label="Team Lead" value={formData.teamLeadName} disabled />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Autocomplete size="small" options={['Low', 'Medium', 'High']} value={formData.priority}
                                    onChange={(_, v) => updateField('priority', v)}
                                    renderInput={(p) => <TextField {...p} label="Priority *" />}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4 }}>
                                <DatePicker
                                    label="Start Date *"
                                    value={formData.startDate}
                                    format="DD/MM/YYYY"
                                    minDate={minAllowedDate}
                                    maxDate={maxAllowedDate || undefined}
                                    onChange={(nv) => updateField('startDate', nv)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!errors.startDate,
                                            helperText: errors.startDate || (minAllowedDate ? `${dateSource} starts: ${minAllowedDate.format('DD/MM/YYYY')}` : '')
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4 }}>
                                <DatePicker
                                    label="End Date *"
                                    value={formData.endDate}
                                    minDate={formData.startDate || minAllowedDate}
                                    maxDate={maxAllowedDate || undefined}
                                    format="DD/MM/YYYY"
                                    onChange={(nv) => updateField('endDate', nv)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!errors.endDate,
                                            helperText: errors.endDate || (maxAllowedDate ? `${dateSource} Deadline: ${maxAllowedDate.format('DD/MM/YYYY')}` : '')
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                                    Story Points *: {formData.storyPoints || 'Select points'}
                                </Typography>
                                <ToggleButtonGroup fullWidth size="small" exclusive value={formData.storyPoints}
                                    onChange={(_, v) => updateField('storyPoints', v ?? 0)}
                                    sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                    {STORY_POINTS_CONFIG.map((pt) => (
                                        <ToggleButton key={pt.value} value={pt.value} sx={{
                                            flex: 1, minWidth: '45px', fontWeight: 700, borderRadius: '4px !important',
                                            '&.Mui-selected': { bgcolor: pt.color, color: 'white', '&:hover': { bgcolor: pt.color } }
                                        }}>{pt.label}</ToggleButton>
                                    ))}
                                </ToggleButtonGroup>

                                {/* Dynamic Meaning & Priority Display */}
                                {formData.storyPoints > 0 && (
                                    <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8fafc', borderLeft: `4px solid ${currentPointDetails?.color}` }}>
                                        <Grid container spacing={1}>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 700 }}>MEANING</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{currentPointDetails?.meaning}</Typography>
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 700 }}>SUGGESTED PRIORITY</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: currentPointDetails?.color }}>
                                                    {formData.storyPoints >= 13 ? 'Critical / High' : formData.storyPoints >= 5 ? 'Medium' : 'Low'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 700, mt: 0.5 }}>EXAMPLE</Typography>
                                                <Typography variant="caption" sx={{ color: '#475569', fontStyle: 'italic' }}>"{currentPointDetails?.example}"</Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                )}
                                {errors.storyPoints && <Typography variant="caption" color="error">{errors.storyPoints}</Typography>}
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <TextField fullWidth multiline rows={2} label="Description *" value={formData.description}
                                    error={!!errors.description} helperText={errors.description || "As a [role], I want [goal], so that [benefit]"}
                                    onChange={(e) => updateField('description', e.target.value)} />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <TextField fullWidth multiline rows={2} label="Acceptance Criteria *" value={formData.acceptanceCriteria}
                                    error={!!errors.acceptanceCriteria} helperText={errors.acceptanceCriteria}
                                    onChange={(e) => updateField("acceptanceCriteria", e.target.value)} size="small" />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ px: 2, pb: 1 }}>
                        {apiError && (
                            <Alert
                                severity="error"
                                onClose={() => setApiError(null)}
                                sx={{
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    '& .MuiAlert-message': { fontWeight: 500 }
                                }}
                            >
                                {/* User-friendly prefix for the API message */}
                                <strong>Budget Conflict:</strong> {apiError}
                            </Alert>
                        )}
                    </Box>

                    <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 2, bgcolor: '#f8fafc' }}>
                        <Button onClick={handleCancelClick} color="inherit" disabled={submitting} sx={{ fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                        <Button onClick={handleSubmit} variant="contained" disabled={submitting}
                            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, fontWeight: 600, px: 4, textTransform: 'none' }}>
                            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Create User Story'}
                        </Button>
                    </Box>
                </Box>
            </LocalizationProvider>
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} sx={{ width: '100%', minWidth: '450px' }}>{snackbar.message}</Alert>
            </Snackbar>

            <MissingDepartmentsDialog
                open={showMissingDeptDialog}
                project={selectedProjectDetails}
                onClose={() => setShowMissingDeptDialog(false)}
                onDepartmentsAdded={handleRefreshProjectDetails}
            />
        </Box>
    );
}

export default CreateUserStory;